import React, { useRef, useState, useEffect } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { blankRealtor, RealtorForm } from "./RealtorForms";
import { parseVcard, fuzzyMatchBrokerage } from "@/lib/vcardParser";

/**
 * Manual Realtor add dialog + vCard/contact-card upload.
 *
 * The Upload button opens the native file picker with an .vcf filter,
 * so it works everywhere:
 *   - iOS: Files app (Contacts → Share → Save to Files exports .vcf)
 *   - Android + Samsung: Contacts export → .vcf, picker returns it
 *   - Desktop: any .vcf saved from Outlook, Apple Contacts, etc.
 *
 * We parse client-side (no network round-trip) and merge the parsed
 * fields over the form's current state. User can still edit every field
 * before saving.
 */
export default function AddRealtorManuallyDialog({
  open,
  onClose,
  onDone,
  brokerages,
}) {
  const [r, setR] = useState(blankRealtor());
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setR(blankRealtor());
      setBusy(false);
    }
  }, [open]);

  const handleFilePick = async (e) => {
    const file = e.target?.files?.[0];
    // Reset the input so the same file can be picked again after a fix.
    e.target.value = "";
    if (!file) return;
    const nameLower = (file.name || "").toLowerCase();
    if (!nameLower.endsWith(".vcf") && !/vcard/.test(file.type || "")) {
      toast.error("That doesn't look like a contact card", {
        description: "Please pick a .vcf file exported from your Contacts app.",
      });
      return;
    }
    try {
      const text = await file.text();
      const { fields, warnings, matched } = parseVcard(text);
      if (matched.length === 0) {
        toast.error("Couldn't read this contact card", {
          description: warnings[0] || "No recognisable fields found.",
        });
        return;
      }
      // Match brokerage_name → brokerage_id via fuzzy match.
      let brokerage_id = r.brokerage_id;
      if (fields.brokerage_name && !brokerage_id) {
        brokerage_id = fuzzyMatchBrokerage(fields.brokerage_name, brokerages || []);
      }
      // Merge over whatever the user has already typed — never clobber a
      // field they've already edited (only fill empties).
      setR((prev) => {
        const merged = { ...prev };
        for (const [k, v] of Object.entries(fields)) {
          if (!v) continue;
          if (!merged[k]) merged[k] = v;
        }
        if (brokerage_id && !merged.brokerage_id) merged.brokerage_id = brokerage_id;
        return merged;
      });
      const bMsg =
        fields.brokerage_name && !brokerage_id
          ? ` — brokerage "${fields.brokerage_name}" wasn't found, please pick one.`
          : "";
      const wMsg = warnings.length ? ` (${warnings[0]})` : "";
      toast.success(`Imported ${matched.length} field${matched.length === 1 ? "" : "s"} from contact card${bMsg}${wMsg}`);
    } catch (err) {
      toast.error("Couldn't read this contact card", {
        description: String(err?.message || err),
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/realtors", r);
      toast.success("Realtor added");
      onDone();
    } catch (err) {
      toast.error("Add failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="add-realtor-dialog"
      >
        <DialogHeader>
          <DialogTitle>Add realtor manually</DialogTitle>
          <DialogDescription>
            All fields are optional except name and a brokerage.
          </DialogDescription>
        </DialogHeader>

        {/* vCard upload — first thing so users see it before scrolling
            through 20 fields. `capture` is intentionally NOT set so
            iOS/Android open the file picker (Files / Drive / Downloads)
            instead of the camera. */}
        <div
          className="mb-1 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2.5 flex items-center gap-3"
          data-testid="add-realtor-vcard-import"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800">
              Have their contact card?
            </p>
            <p className="text-xs text-slate-500">
              Import a <code>.vcf</code> from your phone's Contacts app to
              autofill this form. Works on iPhone, Android and Samsung.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            className="min-h-[40px] shrink-0"
            data-testid="add-realtor-vcard-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".vcf,text/vcard,text/x-vcard,text/directory"
            className="hidden"
            onChange={handleFilePick}
            data-testid="add-realtor-vcard-input"
          />
        </div>

        <RealtorForm r={r} setR={setR} brokerages={brokerages} idPrefix="add" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="min-h-[44px]">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="add-realtor-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Add realtor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
